import * as Sentry from '@sentry/react'
import { Code2, Bug } from 'lucide-react'
import { useEffect, useState, useCallback } from 'react'

import { DashboardBoard } from '@/components/dashboard/DashboardBoard'
import { useDashboard } from '@/hooks/useDashboard'
import { getDeveloperAnalytics, getDeveloperWorkspacePage } from '@/services/api/developer.service'
import type { DashboardAnalyticsResponse, DashboardWidget } from '@/types/api/dashboard'
import { logger } from '@/utils/logger'


function SentryTestButton() {
  return (
    <button
      onClick={() => {
        try {
          throw new Error('This is your first error!')
        } catch (e) {
          Sentry.captureException(e)
        }
      }}
      className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-600 shadow-sm hover:bg-red-50"
    >
      <Bug className="h-3 w-3" />
      Test Sentry
    </button>
  )
}

async function resolveDeveloperWidgetData(widget: DashboardWidget): Promise<DashboardAnalyticsResponse> {
  switch (widget.dataSource) {
    case 'developer.system.summary': {
      const analytics = await getDeveloperAnalytics()
      return {
        metrics: Object.entries(analytics.summary).map(([label, value]) => ({ label, value })),
      }
    }
    case 'developer.workspaces.table': {
      const page = await getDeveloperWorkspacePage({ page: 0, size: 10 })
      return { items: page.content as unknown as DashboardAnalyticsResponse['items'] }
    }
    case 'developer.subscriptions.statusCounts': {
      const analytics = await getDeveloperAnalytics()
      const counts = new Map<string, number>()
      for (const row of analytics.workspaceStatusCounts) {
        const status = String((row as Record<string, unknown>).status ?? 'NONE')
        counts.set(status, (counts.get(status) ?? 0) + 1)
      }
      return { series: Array.from(counts.entries()).map(([label, value]) => ({ label, value })) }
    }
    default:
      return { message: 'Unsupported dashboard data source', dataSource: widget.dataSource }
  }
}

export function DeveloperDashboardPage() {
  const {
    dashboard, createDashboard, updateDashboard, queryAnalytics,
    createWidget, deleteWidget, resizeWidget,
    isSaving, isLoading,
  } = useDashboard()

  const [widgets, setWidgets] = useState<DashboardWidget[]>([])
  const [widgetData, setWidgetData] = useState<Record<string, DashboardAnalyticsResponse | undefined>>({})

  useEffect(() => {
    if (dashboard?.widgets) {
      setWidgets(dashboard.widgets)
    }
  }, [dashboard])

  useEffect(() => {
    let cancelled = false
    async function loadData() {
      const entries = await Promise.all(
        widgets.map(async (widget) => {
          try {
            const data = widget.dataSource.startsWith('developer.')
              ? await resolveDeveloperWidgetData(widget)
              : await queryAnalytics({ dataSource: widget.dataSource, config: widget.dataSourceConfig ?? {} })
            return [widget.id, data] as const
          } catch {
            logger.warn('Failed to load widget data', widget.dataSource)
            return [widget.id, { message: 'Failed to load widget data' } as DashboardAnalyticsResponse] as const
          }
        })
      )
      if (!cancelled) {
        setWidgetData(Object.fromEntries(entries))
      }
    }
    // Only widgets without data are loaded, so new widgetData never triggers a refetch.
    if (widgets.length > 0 && widgets.some(w => !widgetData[w.id])) {
      void loadData()
    }
    return () => {
      cancelled = true
    }
  }, [widgets, queryAnalytics, widgetData])

  const persistSettings = (overrides: Partial<{
    title: string
    description: string | null
  }>) => {
    if (!dashboard) return
    const payload = {
      title: overrides?.title ?? dashboard.title,
      description: overrides?.description ?? dashboard.description,
      isDefault: dashboard.isDefault,
    }
    if (dashboard.id) {
      updateDashboard({ dashboardId: dashboard.id, data: payload })
    } else {
      createDashboard({ ...payload, isDefault: true })
    }
  }

  const handleCreateWidget = useCallback(async (widgetDataToCreate: Omit<DashboardWidget, 'id' | 'createdAt' | 'updatedAt' | 'dashboardId'> & { tempId?: string }) => {
    if (!dashboard?.id) return
    const { tempId, ...rest } = widgetDataToCreate
    try {
      const created = await createWidget({
        dashboardId: dashboard.id,
        ...rest
      })
      setWidgets((prev) => {
        if (tempId && prev.some(w => w.id === tempId)) {
          return prev.map(w => w.id === tempId ? { ...created } : w)
        }
        return [...prev, created]
      })
    } catch (err) {
      logger.error('Failed to create widget', err, 'expected')
    }
  }, [dashboard?.id, createWidget])

  const handleDeleteWidget = useCallback(async (widgetId: string) => {
    if (!dashboard?.id) return
    if (widgetId.startsWith('temp-')) {
      setWidgets((prev) => prev.filter((w) => w.id !== widgetId))
      return
    }
    try {
      await deleteWidget({ dashboardId: dashboard.id, widgetId })
    } catch (err) {
      logger.error('Failed to delete widget', err, 'expected')
    }
  }, [dashboard?.id, deleteWidget])

  const handleResizeWidget = useCallback(async (widgetId: string, w: number, h: number) => {
    if (!dashboard?.id || widgetId.startsWith('temp-')) return
    try {
      await resizeWidget({ dashboardId: dashboard.id, widgetId, w, h })
    } catch (err) { logger.error('Failed to resize widget', err, 'expected') }
  }, [dashboard?.id, resizeWidget])

  return (
    <DashboardBoard
      headerTitle={
        <div className="flex items-center gap-2">
          <Code2 className="h-5 w-5 text-primary-600" />
          <h1 className="text-xl font-bold text-neutral-900">Developer Dashboard</h1>
          <SentryTestButton />
        </div>
      }
      headerDescription="System-wide overview widgets for workspaces, growth, and operational health."
      dashboard={dashboard}
      widgets={widgets}
      widgetData={widgetData}
      isLoading={isLoading}
      isSaving={isSaving}
      onCreateWidget={handleCreateWidget}
      onDeleteWidget={handleDeleteWidget}
      onResizeWidget={handleResizeWidget}
      onWidgetsChange={setWidgets}
      onSaveSettings={(updates) =>
        persistSettings({
          title: updates.title,
          description: updates.description,
        })
      }
    />
  )
}
